import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  getAllOnlineOrders,
  confirmOnlineOrder,
  getSpecificOnlineOrder,
  updateOnlineOrderItems,
  rejectOnlineOrder,
  updateOnlineOrderStatus,
  getOrderStatusCounts,
} from "../../../actions/ecommerce/onlineOrderAction";
import EcommerceOrderFilter from "./EcommerceOrderFilter";
import { getProductSearchResultforEcommerce } from "../../../actions/productDetails";
import { getContactInvoicesSummaryByPhone } from "../../../actions/contactAction";
import { Layout, message, Modal, Breadcrumb, Tabs } from "antd";
import { InboxOutlined, ShoppingCartOutlined, PhoneOutlined, CheckCircleOutlined, CloseCircleOutlined, HistoryOutlined, FileTextOutlined } from "@ant-design/icons";

// components
import OrdersTable from "./components/OrdersTable";
import OrderDetailsModal from "./components/OrderDetailsModal";
import EditItemsModal from "./components/EditItemsModal";
import OrderStatusTab from "./components/OrderStatusTab";
import CustomerSummary from "./components/CustomerSummary";

const { Content } = Layout;
const { TabPane } = Tabs;

const OnlineOrders = ({
  getAllOnlineOrders,
  confirmOnlineOrder,
  getSpecificOnlineOrder,
  updateOnlineOrderItems,
  rejectOnlineOrder,
  getProductSearchResultforEcommerce,
  getContactInvoicesSummaryByPhone,
  updateOnlineOrderStatus,
  getOrderStatusCounts,
}) => {
  const formatter = new Intl.NumberFormat("en-IN");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [customerSummary, setCustomerSummary] = useState(null);
  const [loadingCustomerSummary, setLoadingCustomerSummary] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [editingItems, setEditingItems] = useState([]);
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProduct, setSearchingProduct] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSearchIndex, setCurrentSearchIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("order");
  const [statusCounts, setStatusCounts] = useState({});
  const [activeStatusTab, setActiveStatusTab] = useState("all");

  // Pagination and filter state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({});

  const orderStatusOptions = [
    { value: 'all', label: 'All Orders', color: 'default', icon: <InboxOutlined /> },
    { value: 'New Order', label: 'New Order', color: 'red', icon: <InboxOutlined /> },
    { value: 'Processing', label: 'Processing', color: 'default', icon: <ShoppingCartOutlined /> },
    { value: 'Already Called', label: 'Already Called', color: 'default', icon: <PhoneOutlined /> },
    { value: 'Call Not Picked', label: 'Call Not Picked', color: 'default', icon: <PhoneOutlined /> },
    { value: 'Waiting', label: 'Waiting', color: 'default', icon: <HistoryOutlined /> },
    { value: 'Confirmed', label: 'Confirmed', color: 'default', icon: <CheckCircleOutlined /> },
    { value: 'Cancelled', label: 'Cancelled', color: 'default', icon: <CloseCircleOutlined /> },
    { value: 'Others', label: 'Others', color: 'default', icon: <FileTextOutlined /> },
  ];

  useEffect(() => {
    fetchStatusCounts();
    fetchOrders();
  }, []);

  const fetchStatusCounts = async () => {
    try {
      const response = await getOrderStatusCounts();
      if (response && response.success) {
        setStatusCounts(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch status counts:", error);
    }
  };

  const fetchOrders = async (page = 1, pageSize = 10, filterParams = {}) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        page_size: pageSize,
        ...filterParams,
      };

      const response = await getAllOnlineOrders(params);

      if (response && response.results) {
        setData(response.results || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.count || 0,
        });
      } else {
        setData(response || []);
        setPagination({
          current: 1,
          pageSize: pageSize,
          total: response ? response.length : 0,
        });
      }
    } catch (error) {
      message.error("Failed to fetch orders");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchOrders(newPagination.current, newPagination.pageSize, filters);
  };

  const handleFilter = (filterValues) => {
    setFilters(filterValues);
    fetchOrders(1, pagination.pageSize, filterValues);
  };

  const handleStatusTabChange = (key) => {
    setActiveStatusTab(key);
    const newFilters = { ...filters };

    if (key === 'all') {
      delete newFilters.order_status;
    } else {
      newFilters.order_status = key;
    }

    setFilters(newFilters);
    fetchOrders(1, pagination.pageSize, newFilters);
  };

  const handleViewCustomerSummary = async (phone) => {
    if (!phone) {
      message.warning("No phone number available for this customer");
      return;
    }

    setLoadingCustomerSummary(true);
    try {
      const summary = await getContactInvoicesSummaryByPhone(phone);
      setCustomerSummary(summary);
      setActiveTab("customer");
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setCustomerSummary({
          invoices: [],
          invoice_summary: []
        });
        setActiveTab("customer");
      } else {
        message.error("Failed to fetch customer invoice summary");
        setCustomerSummary(null);
      }
    } finally {
      setLoadingCustomerSummary(false);
    }
  };

  const handleViewDetails = async (record) => {
    setLoading(true);
    setActiveTab("order");
    try {
      const orderDetails = await getSpecificOnlineOrder(record.id);
      setSelectedOrder(orderDetails);
      setDetailsVisible(true);
      setCustomerSummary(null);

      const orderData = orderDetails.data || {};
      const contact = orderDetails.contact || orderData.contact || {};
      if (contact.phone) {
        handleViewCustomerSummary(contact.phone);
      }
    } catch (error) {
      message.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (record) => {
    if (!record.invoice_number) {
      message.error("Invoice number not found");
      return;
    }

    window.location.href = `/ecommerce-online-order/${record.invoice_number}`;
  };

  const handleEditItems = () => {
    const items = (selectedOrder && selectedOrder.items) || (selectedOrder && selectedOrder.data && selectedOrder.data.items) || [];
    setEditingItems(
      items.map((item, index) => ({
        item_index: index,
        product_location_id: (item.product_location && item.product_location.id) || item.product,
        quantity: item.quantity,
        price: item.price,
        spec_discount: item.spec_discount || 0,
        product_name: item.product_name || (item.product_details && item.product_details.name) || "N/A",
        current_product: item.product_location || {},
        warehouse_name: item.warehouse ? item.warehouse.name : (item.product_location && item.product_location.warehouse ? item.product_location.warehouse.name : "N/A"),
      }))
    );
    setEditModalVisible(true);
    setSearchTerm("");
    setProductSearchResults([]);
  };

  const handleSearchProduct = async (value, itemIndex) => {
    if (!value || value.trim().length < 2) {
      setProductSearchResults([]);
      return;
    }

    setSearchTerm(value);
    setCurrentSearchIndex(itemIndex);
    setSearchingProduct(true);
    try {
      const locationId = (selectedOrder && selectedOrder.data && selectedOrder.data.location_id) || "";
      const response = await getProductSearchResultforEcommerce(
        value.trim(),
        locationId,
        1,
        20
      );

      const transformedResults = (response && response.results) ? response.results.map(product => ({
        ...product,
        warehouse_name: product.warehouse ? product.warehouse.name :
          (product.Warehouse ? product.Warehouse.name :
            (product.ProductLocation && product.ProductLocation.warehouse ?
              product.ProductLocation.warehouse.name : "N/A")),
        display_name: `${product.title || product.name} - ${product.selling_price || product.price || 0}`
      })) : [];

      setProductSearchResults(transformedResults);
    } catch (error) {
      message.error("Failed to search products");
      setProductSearchResults([]);
    } finally {
      setSearchingProduct(false);
    }
  };

  const handleProductSelect = (productId, itemIndex) => {
    const product = productSearchResults.find((p) => p.id === productId);

    if (product) {
      const newItems = [...editingItems];
      const warehouseName = product.Warehouse_name ||
        product.warehouse_name ||
        (product.warehouse && product.warehouse.name) ||
        (product.Warehouse && product.Warehouse.name) ||
        (product.ProductLocation && product.ProductLocation.warehouse && product.ProductLocation.warehouse.name) ||
        "N/A";

      newItems[itemIndex] = {
        ...newItems[itemIndex],
        product_location_id: product.id,
        price: product.selling_price || product.price || 0,
        product_name: product.title || product.name || "N/A",
        current_product: product,
        warehouse_name: warehouseName,
        Warehouse_name: warehouseName,
      };
      setEditingItems(newItems);

      setProductSearchResults([]);
      setSearchTerm("");
    }
  };

  const handleUpdateItems = async () => {
    try {
      setConfirmLoading(true);

      const formattedItems = editingItems.map(item => ({
        item_index: item.item_index,
        product_location_id: item.product_location_id,
        quantity: item.quantity,
        price: item.price,
        spec_discount: item.spec_discount || 0,
      }));

      const response = await updateOnlineOrderItems(
        selectedOrder.id,
        formattedItems
      );

      if (response.success) {
        message.success(response.message);
        setEditModalVisible(false);
        const updatedOrder = await getSpecificOnlineOrder(selectedOrder.id);
        setSelectedOrder(updatedOrder);
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error("Failed to update items");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirmOrder = async (orderId) => {
    Modal.confirm({
      title: "Confirm Order",
      content: "Are you sure you want to confirm this order?",
      okText: "Yes, Confirm",
      cancelText: "Cancel",
      onOk: async () => {
        setConfirmLoading(true);
        try {
          const response = await confirmOnlineOrder(orderId);

          if (response.success) {
            if (response.partial) {
              Modal.warning({
                title: "Partial Order Confirmation",
                width: 700,
                content: (
                  <div>
                    <p style={{ marginBottom: 12 }}>
                      <strong>Invoice created for available items only.</strong>
                    </p>
                    <p style={{ marginBottom: 8 }}>
                      Items confirmed: <strong>{response.items_confirmed}</strong>
                    </p>
                    <p style={{ marginBottom: 12 }}>
                      Items not available: <strong>{response.items_not_available}</strong>
                    </p>
                    {response.unavailable_items &&
                      response.unavailable_items.length > 0 && (
                        <div
                          style={{
                            background: "#fff7e6",
                            border: "1px solid #ffd591",
                            borderRadius: 4,
                            padding: 12,
                            marginTop: 12,
                          }}
                        >
                          <p style={{ fontWeight: "bold", marginBottom: 12 }}>
                            Items not included due to insufficient stock:
                          </p>
                          <div
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              marginBottom: 8,
                            }}
                          >
                            {response.unavailable_items.map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  background: "#fff",
                                  border: "1px solid #ffd591",
                                  borderRadius: 4,
                                  padding: 12,
                                  marginBottom: 8,
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: 14,
                                    marginBottom: 6,
                                    color: "#333",
                                  }}
                                >
                                  SKU: {item.product_title || item.product}
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "8px",
                                    fontSize: 12,
                                  }}
                                >
                                  <div>
                                    <span style={{ color: "#666" }}>
                                      Attributes:{" "}
                                    </span>
                                    <span style={{ fontWeight: 500 }}>
                                      {item.attributes}
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: "#666" }}>
                                      Warehouse:{" "}
                                    </span>
                                    <span style={{ fontWeight: 500 }}>
                                      {item.warehouse}
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: "#666" }}>
                                      Available:{" "}
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#ff4d4f",
                                      }}
                                    >
                                      {item.available}
                                    </span>
                                  </div>
                                  <div>
                                    <span style={{ color: "#666" }}>
                                      Required:{" "}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>
                                      {item.required}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    <div
                      style={{
                        marginTop: 16,
                        paddingTop: 12,
                        borderTop: "1px solid #d9d9d9",
                      }}
                    >
                      <p style={{ margin: 0, fontSize: 13, color: "#666" }}>
                        Invoice #{response.invoice_number} | Order #
                        {response.order_number}
                      </p>
                    </div>
                  </div>
                ),
                onOk: () => {
                  fetchOrders(pagination.current, pagination.pageSize, filters);
                  fetchStatusCounts();
                  setDetailsVisible(false);
                },
              });
            } else {
              message.success(response.message);
              fetchOrders(pagination.current, pagination.pageSize, filters);
              fetchStatusCounts();
              setDetailsVisible(false);
            }
          } else {
            message.error(response.message);
          }
        } catch (error) {
          const errorMessage =
            (error &&
              error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Failed to confirm order";

          if (errorMessage.includes("No products available in stock")) {
            const errorParts = errorMessage.split(
              "No products available in stock."
            );
            const detailedError = errorParts[1] ? errorParts[1].trim() : "";
            const productErrors = detailedError.split(";").filter((e) => e.trim());

            Modal.error({
              title: "Order Confirmation Failed",
              width: 700,
              content: (
                <div>
                  <p style={{ marginBottom: 12 }}>
                    <strong>
                      Cannot confirm order - no products available in stock.
                    </strong>
                  </p>
                  {productErrors.length > 0 ? (
                    <div
                      style={{
                        background: "#43434330",
                        border: "1px solid #a9a9a929",
                        borderRadius: 4,
                        padding: 12,
                        maxHeight: "350px",
                        overflowY: "auto",
                      }}
                    >
                      <p
                        style={{
                          fontWeight: "bold",
                          marginBottom: 12,
                          color: "#f5414179",
                        }}
                      >
                        Products out of stock:
                      </p>
                      {productErrors.map((error, index) => {

                        const match = error.match(
                          /^(.+?)\s+-\s+(.+?)\s+-\s+Warehouse:\s+(.+?)\s+\(Available:\s+(\d+),\s+Required:\s+(\d+)\)$/
                        );

                        if (match) {
                          const [
                            ,
                            productTitle,
                            attributes,
                            warehouse,
                            available,
                            required,
                          ] = match;

                          return (
                            <div
                              key={index}
                              style={{
                                background: "#fff",
                                border: "1px solid #ffccc7",
                                borderRadius: 4,
                                padding: 12,
                                marginBottom: 8,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: 14,
                                  marginBottom: 6,
                                  color: "#333",
                                }}
                              >
                                SKU: {productTitle.trim()}
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "8px",
                                  fontSize: 12,
                                }}
                              >
                                <div>
                                  <span style={{ color: "#666" }}>
                                    Attributes:{" "}
                                  </span>
                                  <span style={{ fontWeight: 500 }}>
                                    {attributes.trim()}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: "#666" }}>
                                    Warehouse:{" "}
                                  </span>
                                  <span style={{ fontWeight: 500 }}>
                                    {warehouse.trim()}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: "#666" }}>
                                    Available:{" "}
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#cf1322",
                                    }}
                                  >
                                    {available}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: "#666" }}>
                                    Required:{" "}
                                  </span>
                                  <span style={{ fontWeight: 600 }}>
                                    {required}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Fallback if parsing fails
                        return (
                          <div
                            key={index}
                            style={{
                              background: "#fff",
                              border: "1px solid #ffccc7",
                              borderRadius: 4,
                              padding: 8,
                              marginBottom: 6,
                              fontSize: 13,
                            }}
                          >
                            {error.trim()}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "#fff2f0",
                        border: "1px solid #ffccc7",
                        borderRadius: 4,
                        padding: 12,
                      }}
                    >
                      <p style={{ margin: 0, fontSize: 13 }}>
                        {detailedError || "Please check product availability."}
                      </p>
                    </div>
                  )}
                </div>
              ),
            });
          } else {
            message.error(errorMessage);
          }
        } finally {
          setConfirmLoading(false);
        }
      },
    });
  };

  const handleRejectOrder = async (orderId) => {
    Modal.confirm({
      title: "Reject Order",
      content: "Are you sure you want to reject this order?",
      okText: "Yes, Reject",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        setConfirmLoading(true);
        try {
          const response = await rejectOnlineOrder(orderId);
          if (response.success) {
            message.success(response.message);
            fetchOrders();
            setDetailsVisible(false);
          } else {
            message.error(response.message);
          }
        } catch (error) {
          message.error("Failed to reject order");
        } finally {
          setConfirmLoading(false);
        }
      },
    });
  };

  const handleUpdateOrderStatus = async (values) => {
    try {
      const response = await updateOnlineOrderStatus(selectedOrder.id, values);
      message.success("Order status updated successfully");

      const updatedOrder = await getSpecificOnlineOrder(selectedOrder.id);
      setSelectedOrder(updatedOrder);

      fetchOrders(pagination.current, pagination.pageSize, filters);
      fetchStatusCounts();

      return response;
    } catch (error) {
      message.error("Failed to update order status");
      throw error;
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "24px" }}>
        <Breadcrumb style={{ marginBottom: "16px" }}>
          <Breadcrumb.Item>Home</Breadcrumb.Item>
          <Breadcrumb.Item>E-commerce</Breadcrumb.Item>
          <Breadcrumb.Item>Online Orders</Breadcrumb.Item>
        </Breadcrumb>

        <EcommerceOrderFilter onFilter={handleFilter} loading={loading} />

        <Tabs
          activeKey={activeStatusTab}
          onChange={handleStatusTabChange}
          type="card"
          style={{ marginTop: 16, marginBottom: -16 }}
        >
          {orderStatusOptions.map((option) => {
            const count =
              option.value === 'all'
                ? statusCounts.total || 0
                : (statusCounts.by_status && statusCounts.by_status[option.value]) || 0;

            return (
              <TabPane
                key={option.value}
                tab={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <span>
                      {option.label} <strong>({count})</strong>
                    </span>
                  </span>
                }
              >
                <OrdersTable
                  data={data}
                  loading={loading}
                  pagination={pagination}
                  filters={filters}
                  onTableChange={handleTableChange}
                  onViewDetails={handleViewDetails}
                  onOrderClick={handleOrderClick}
                />
              </TabPane>
            );
          })}
        </Tabs>

        <OrderDetailsModal
          visible={detailsVisible}
          selectedOrder={selectedOrder}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onCancel={() => {
            setDetailsVisible(false);
            setCustomerSummary(null);
            setActiveTab("order");
          }}
          onEditItems={handleEditItems}
          onConfirmOrder={handleConfirmOrder}
          onRejectOrder={handleRejectOrder}
          confirmLoading={confirmLoading}
          customerSummary={customerSummary}
          loadingCustomerSummary={loadingCustomerSummary}
          renderCustomerSummary={() => (
            <CustomerSummary
              customerSummary={customerSummary}
              loadingCustomerSummary={loadingCustomerSummary}
            />
          )}
          OrderStatusTab={OrderStatusTab}
          handleUpdateOrderStatus={handleUpdateOrderStatus}
          loading={loading}
        />

        <EditItemsModal
          visible={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            setProductSearchResults([]);
            setSearchTerm("");
          }}
          onOk={handleUpdateItems}
          confirmLoading={confirmLoading}
          editingItems={editingItems}
          setEditingItems={setEditingItems}
          productSearchResults={productSearchResults}
          searchingProduct={searchingProduct}
          currentSearchIndex={currentSearchIndex}
          onSearchProduct={handleSearchProduct}
          onProductSelect={handleProductSelect}
        />
      </Content>
    </Layout>
  );
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {
  getAllOnlineOrders,
  confirmOnlineOrder,
  getSpecificOnlineOrder,
  updateOnlineOrderItems,
  rejectOnlineOrder,
  getProductSearchResultforEcommerce,
  getContactInvoicesSummaryByPhone,
  updateOnlineOrderStatus,
  getOrderStatusCounts
})(OnlineOrders);